import { motion } from "framer-motion";

interface HalEyeProps {
  size?: "sm" | "md" | "lg" | "fab";
  color?: string;
  isActive?: boolean;
}

const sizeConfig = {
  sm:  { outer: 28, inner: 10, dot: 3,  glow: 6,  rings: 1 },
  md:  { outer: 44, inner: 14, dot: 5,  glow: 10, rings: 2 },
  lg:  { outer: 56, inner: 20, dot: 6,  glow: 14, rings: 3 },
  fab: { outer: 48, inner: 16, dot: 5,  glow: 12, rings: 2 },
};

export function HalEye({ size = "md", color = "#ef4444", isActive = true }: HalEyeProps) {
  const s = sizeConfig[size];
  const ringSpacing = (s.outer - s.inner) / (s.rings + 1);

  return (
    <div
      className="relative rounded-full flex items-center justify-center overflow-hidden"
      style={{
        width: s.outer,
        height: s.outer,
        background: `radial-gradient(circle, #111118 0%, #08080e 50%, #020206 100%)`,
      }}
    >
      {/* Outer chrome bezel */}
      <div
        className="absolute inset-0 rounded-full"
        style={{
          border: `1px solid rgba(255,255,255,0.08)`,
          boxShadow: `inset 0 1px 2px rgba(255,255,255,0.05), inset 0 -1px 2px rgba(0,0,0,0.3)`,
        }}
      />

      {/* Concentric lens rings — like real camera optics */}
      {Array.from({ length: s.rings }).map((_, i) => {
        const ringSize = s.inner + ringSpacing * (i + 1) * 2;
        return (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              width: ringSize,
              height: ringSize,
              border: `0.5px solid ${color}${i === 0 ? "18" : "0c"}`,
            }}
            animate={isActive ? {
              opacity: [0.4, 0.7, 0.4],
              scale: [1, 1.01, 1],
            } : {}}
            transition={{
              duration: 5 + i,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.8,
            }}
          />
        );
      })}

      {/* Iris — warm radial gradient */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: s.inner,
          height: s.inner,
          background: `radial-gradient(circle at 45% 42%, ${color}ee 0%, ${color}aa 30%, ${color}55 60%, ${color}18 85%, transparent 100%)`,
          boxShadow: `0 0 ${s.glow}px ${color}90, 0 0 ${s.glow * 2.5}px ${color}35`,
        }}
        animate={isActive ? {
          scale: [1, 1.06, 0.97, 1.03, 1],
          opacity: [0.85, 1, 0.8, 0.95, 0.85],
        } : {}}
        transition={{
          duration: 5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Core — hot white center */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: s.dot,
          height: s.dot,
          background: `radial-gradient(circle, #fff 0%, ${color} 50%, transparent 100%)`,
          boxShadow: `0 0 ${s.dot * 1.5}px #fff, 0 0 ${s.dot * 3}px ${color}cc, 0 0 ${s.dot * 5}px ${color}40`,
        }}
        animate={isActive ? {
          scale: [1, 1.4, 0.9, 1.2, 1],
          opacity: [0.7, 1, 0.6, 0.9, 0.7],
        } : {}}
        transition={{
          duration: 3.5,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Specular highlight — lens reflection */}
      <div
        className="absolute rounded-full"
        style={{
          width: s.dot * 0.5,
          height: s.dot * 0.5,
          top: "28%",
          left: "60%",
          background: "rgba(255,255,255,0.6)",
          filter: "blur(0.4px)",
        }}
      />

      {/* Secondary faint highlight */}
      <div
        className="absolute rounded-full"
        style={{
          width: s.dot * 0.3,
          height: s.dot * 0.3,
          top: "62%",
          left: "35%",
          background: "rgba(255,255,255,0.15)",
          filter: "blur(0.6px)",
        }}
      />

      {/* Ambient outer glow — breathing */}
      <motion.div
        className="absolute rounded-full"
        style={{
          width: s.outer + 8,
          height: s.outer + 8,
          boxShadow: `0 0 ${s.glow * 2}px ${color}55, 0 0 ${s.glow * 5}px ${color}18`,
        }}
        animate={isActive ? {
          opacity: [0.4, 0.9, 0.4],
          scale: [0.98, 1.02, 0.98],
        } : { opacity: 0.2 }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Scan line sweep — cinematic touch */}
      {isActive && size !== "sm" && (
        <motion.div
          className="absolute inset-0 rounded-full overflow-hidden pointer-events-none"
          style={{ opacity: 0.06 }}
        >
          <motion.div
            style={{
              width: "100%",
              height: "2px",
              background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
            }}
            animate={{ y: [0, s.outer, 0] }}
            transition={{
              duration: 6,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        </motion.div>
      )}
    </div>
  );
}
